import { fmt } from '../api.ts';
import OptionInput from './OptionInput.tsx';
import { faceChoosable } from '../lib/boxTypes.ts';
import {
  FLUTES, PAPER_LABEL, PLY_CHOICES, layerName, plyName, roleOf,
  type LayerDraft, type LayerRow, type Paper,
} from '../lib/layers.ts';

/** The board on the card, as layer 1 shows it when the face is printed on board. */
export interface FaceBoard { name: string; size: string; sheets: number | string }

const gsmOf = (name: string) => name.match(/(\d+(?:\.\d+)?)\s*GSM/i)?.[1] ?? '';

/**
 * Ply and kraft printing above the layer table: what decides which rows there are.
 * A ply the box type states is fixed; an old card's even ply shows until it is changed.
 */
export function PlyPicker({ ply, boxPly, kraftPrinting, make, onPly, onKraft }: {
  ply: number | null; boxPly: number | null; kraftPrinting: boolean; make?: string | null;
  onPly: (ply: number | null) => void; onKraft: (on: boolean) => void;
}) {
  const shown = boxPly ?? ply;
  const old = shown !== null && !(PLY_CHOICES as readonly number[]).includes(shown);
  return (
    <div className="ly-pick">
      <div className="field">
        <label htmlFor="ly-ply" className={boxPly ? undefined : 'req'}>Ply</label>
        {boxPly
          ? <div className="jcs-val">{plyName(boxPly)} <span className="dim">· from box type</span></div>
          : (
            <select id="ly-ply" value={ply ?? ''} onChange={(e) => onPly(e.target.value ? Number(e.target.value) : null)}>
              <option value="">Choose…</option>
              {PLY_CHOICES.map((p) => <option key={p} value={p}>{plyName(p)}</option>)}
              {old && <option value={shown}>{shown} ply (old card)</option>}
            </select>
          )}
      </div>
      <div className="field">
        <label htmlFor="ly-face">Layer 1</label>
        {make === 'KRAFT_PRINT' ? (
          <div className="jcs-val">Printed kraft</div>
        ) : (faceChoosable(make) || (kraftPrinting && make !== 'MONO')) ? (
          <select id="ly-face" value={kraftPrinting ? 'KRAFT' : 'BOARD'}
                  onChange={(e) => onKraft(e.target.value === 'KRAFT')}>
            <option value="BOARD">Board</option>
            <option value="KRAFT">Kraft</option>
          </select>
        ) : <div className="jcs-val">Board</div>}
      </div>
      {!boxPly && <div className="hint ly-mono">Mono carton? Take Corrugation and Flute Pasting off the route — then there is no corrugation card.</div>}
    </div>
  );
}

/** The layers as inputs: one row per layer of the ply. */
export function LayersEditor({ rows, kraftPrinting, board, onChange }: {
  rows: LayerDraft[]; kraftPrinting: boolean; board: FaceBoard | null;
  onChange: (rows: LayerDraft[]) => void;
}) {
  const edit = (k: number, p: Partial<LayerDraft>) =>
    onChange(rows.map((r) => (r.layer === k ? { ...r, ...p } : r)));
  const showBoard = !kraftPrinting && rows.length > 0;
  return (
    <table className="cc-liners ly-table">
      <thead>
        <tr>
          <th style={{ width: 24 }}>#</th><th style={{ width: 96 }}>Layer</th><th style={{ width: 128 }}>Paper</th>
          <th style={{ width: 76 }}>Flute</th><th style={{ width: 128 }}>Size</th><th style={{ width: 96 }}>Qty</th>
          <th style={{ width: 96 }} className="req-h">GSM</th><th style={{ width: 84 }} className="req-h">BF</th><th>Notes</th>
        </tr>
      </thead>
      <tbody>
        {showBoard && (
          <tr className="ly-board" title="Set in Quantity and board">
            <td className="dim">1</td><td>Board</td>
            <td><div className="ly-fixed" title={board?.name}>{board ? board.name.split(' · ')[0] : 'Not chosen yet'}</div></td>
            <td><div className="ly-fixed">—</div></td>
            <td><div className="ly-fixed">{board?.size ?? '—'}</div></td>
            <td><div className="ly-fixed">{board ? fmt(String(board.sheets)) : '—'}</div></td>
            <td><div className="ly-fixed">{board ? gsmOf(board.name) || '—' : '—'}</div></td>
            <td><div className="ly-fixed">—</div></td>
            <td><div className="ly-fixed ly-note">Set in Quantity and board</div></td>
          </tr>
        )}
        {rows.map((r) => {
          const role = roleOf(r.layer, kraftPrinting);
          return (
            <tr key={r.layer}>
              <td className="dim">{r.layer}</td>
              <td>{role === 'KRAFT_FACE' ? 'Kraft face' : role === 'FLUTE' ? 'Flute' : 'Liner'}</td>
              <td>
                {role === 'LINER' ? (
                  <select aria-label={`Layer ${r.layer} paper`} value={r.material}
                          onChange={(e) => edit(r.layer, { material: e.target.value as Paper })}>
                    {(Object.keys(PAPER_LABEL) as Paper[]).map((m) => <option key={m} value={m}>{PAPER_LABEL[m]}</option>)}
                  </select>
                ) : <span className="dim">Kraft</span>}
              </td>
              <td>
                {role === 'FLUTE' ? (
                  <select aria-label={`Layer ${r.layer} flute`} value={r.flute}
                          onChange={(e) => edit(r.layer, { flute: e.target.value })}>
                    <option value="">—</option>
                    {FLUTES.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                ) : <span className="dim">—</span>}
              </td>
              <td><input type="text" aria-label={`Layer ${r.layer} size`} value={r.linerSize}
                         onChange={(e) => edit(r.layer, { linerSize: e.target.value })} /></td>
              <td><input type="number" aria-label={`Layer ${r.layer} qty`} value={r.linerQty}
                         onChange={(e) => edit(r.layer, { linerQty: e.target.value })} /></td>
              <td><OptionInput list="gsm" type="number" value={r.gsm}
                               onChange={(v) => edit(r.layer, { gsm: v })} /></td>
              <td><OptionInput list="bf" type="number" value={r.bf}
                               onChange={(v) => edit(r.layer, { bf: v })} /></td>
              <td><input type="text" aria-label={`Layer ${r.layer} notes`} value={r.notes}
                         onChange={(e) => edit(r.layer, { notes: e.target.value })} /></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** The layers as the card reads them, board first. */
export function LayersView({ ply, kraftPrinting, rows, board }: {
  ply: number | null; kraftPrinting: boolean; rows: LayerRow[]; board: FaceBoard | null;
}) {
  const at = (k: number) => rows.find((r, i) => (r.sequenceNo ?? i + 2) === k);
  const count = Math.max(ply ?? 0, ...rows.map((r, i) => r.sequenceNo ?? i + 2));
  const numbers = Array.from({ length: count }, (_, i) => i + 1);
  const val = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : fmt(String(v)));
  return (
    <div className="tbl">
      <table className="fit ly-view">
        <thead>
          <tr>
            <th>#</th><th>Layer</th><th>Size</th><th className="num">Qty</th>
            <th className="num">GSM</th><th className="num">BF</th>
          </tr>
        </thead>
        <tbody>
          {numbers.map((k) => {
            if (k === 1 && !kraftPrinting) {
              return (
                <tr key={k} className="dim">
                  <td>1</td>
                  <td title={board?.name}>Board{board ? ` · ${board.name.split(' · ')[0]}` : ''}</td>
                  <td>{board?.size ?? '—'}</td>
                  <td className="num">{board ? fmt(String(board.sheets)) : '—'}</td>
                  <td className="num">{board ? gsmOf(board.name) || '—' : '—'}</td>
                  <td className="num">—</td>
                </tr>
              );
            }
            const r = at(k);
            const missing = !r || r.gsm === null || r.bf === null;
            return (
              <tr key={k}>
                <td className="dim">{k}</td>
                <td>
                  {layerName(k, kraftPrinting, r)}
                  {r?.notes ? <span className="dim" title={r.notes}> · {r.notes}</span> : null}
                </td>
                <td>{r?.linerSize ?? '—'}</td>
                <td className="num">{val(r?.linerQty)}</td>
                <td className={`num${missing && !(r?.gsm) ? ' ly-missing' : ''}`}>{val(r?.gsm)}</td>
                <td className={`num${missing && !(r?.bf) ? ' ly-missing' : ''}`}>{val(r?.bf)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
